import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	formatDateToShow,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EmployeeService } from "../../services/EmployeeService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IPaydayAdvance } from "../../models/PaydayAdvance";
import { PaydayAdvanceInfoSetupFieldsPanel } from "./Panels/PaydayAdvanceInfoSetupFieldsPanel";
import { PaydayAdvanceService } from "../../services/PaydayAdvanceService";
import { PaydayAdvanceSetupToolbar } from "./PaydayAdvanceSetupToolbar/PaydayAdvanceSetupToolbar";
import { PaydayAdvanceSetupValidationSchema } from "./validator";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useGetSalaryProfilePermissionData } from "../SalaryProfile";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";







export interface IPaydayAdvanceSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "paydayAdvance-single-data-record";
const PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY = "paydayAdvance-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return PaydayAdvanceService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false
	});

const usePaydayAdvance = (
	clientId: string,
	// employeeId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {

			return PaydayAdvanceService.getPaydayAdvance(
				clientId,
				// employeeId,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-ddl"],
		queryFn: (data) => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false
	});

export const PaydayAdvanceSetup: React.FC<IPaydayAdvanceSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [paydayAdvanceId, setPaydayAdvanceId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "paidBy",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "paidBy",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);


	const DEFAULT_VALUES = {
		employeeId: undefined,
		dateNo: null,
		amount: null,
		paidBy: undefined,
		chequeNo: undefined,
		chequeDate: null,
		notes: undefined,
		printOnCheque: false,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(PaydayAdvanceSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const employeeId = formMethods.watch("employeeId");

	const { isPending, data } = usePaydayAdvance(
		clientId,
		// employeeId,
		queryParams,
		navigateToLogout
	);

	const paydayAdvanceList: any | null = data;

	const { isPending: isPendingEmployeeDDL, data: employeeDDL } =
		useGetEmployeeDDL(clientId);

	const { isPending: isPendingSalaryProfilePermissionsData, data: salaryProfilePermissionsData }: any =
		useGetSalaryProfilePermissionData(
			clientId,
			navigateToLogout,
			selectedEmployee,
			null,
		);

	const { isPending: isSubmitting, mutate: submitPaydayAdvanceFormFields } =
		useMutation({
			mutationFn: PaydayAdvanceService.create,
			onSuccess: (res: IPaydayAdvance | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIPaydayAdvanceResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/employee/payday-advance/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({ queryKey: [PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY] }),
						queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] }),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIPaydayAdvanceResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/employee/payday-advance/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({ queryKey: [PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY] }),
						queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] }),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const upadtedPaydayAdvanceListItems = useMemo(() => {
		return (
			paydayAdvanceList &&
			paydayAdvanceList.items &&
			paydayAdvanceList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/employee/payday-advance/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						Advance
					</Link>
				),
				dateNo:
					item.dateNo && item.dateNo !== "" && formatDateToShow(item.dateNo),
				paidBy:
					item.paidBy === 200
						? "Cheque"
						: item.paidBy === 100
							? "Cash"
							: "Direct Deposit",
				status: "-",
				amount: item.amount && parseFloat(item.amount).toFixed(2),
			}))
		);
	}, [paydayAdvanceList]);

	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			employeeId: values.employeeId,
			dateNo: formatDate(values.dateNo),
			amount: values.amount,
			paidBy: values.paidBy,
			chequeNo: values.chequeNo,
			chequeDate: formatDate(values.chequeDate),
			notes: values.notes !== "" ? values.notes : undefined,
			printOnCheque: values.printOnCheque,
		};
		submitPaydayAdvanceFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === 'dateNo' && fieldValue !== null && fieldValue !== '') {
					formMethods.setValue(fieldName, new Date(fieldValue));
				}
				if (fieldName === 'chequeDate' && fieldValue) {
					formMethods.setValue(fieldName, new Date(fieldValue));
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deletePaydayAdvanceById } =
		useMutation({
			mutationFn: PaydayAdvanceService.delPaydayAdvance,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the PaydayAdvance fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/employee/payday-advance`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY] });
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deletePaydayAdvanceField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				paydayAdvanceId: paydayAdvanceId,
				navigateToLogout: navigateToLogout,
			};
			deletePaydayAdvanceById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = (params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord && selectedDataRecord.id) {
			setPaydayAdvanceId(selectedDataRecord.id);
		} else {
			setPaydayAdvanceId(params.id)
		}
	};

	const { isPending: isArchive, mutate: archivePaydayAdvanceById } =
		useMutation({
			mutationFn: PaydayAdvanceService.archivePaydayAdvance,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the PaydayAdvance fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/employee/payday-advance`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [PAYDAY_ADVANCE_LIST_DATA_QUERY_KEY] });
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archivePaydayAdvanceField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				paydayAdvanceId: paydayAdvanceId,
				navigateToLogout: navigateToLogout,
			};
			archivePaydayAdvanceById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setPaydayAdvanceId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/employee/payday-advance`;
		navigate(path);
	};

	const expandedLabels = ["Employee", "Payday Advance"];
	const expandedPaydayAdvanceSetupOrSetupItem = "Payday Advance"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "PaydayAdvance" &&
				item.label === expandedPaydayAdvanceSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedPaydayAdvanceSetupOrSetupItem && "/employee/payday-advance",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		paidBy: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IPaydayAdvance | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/employee/payday-advance/${params.value}`;
		navigate(path);
	}, []);

	const onEmployeeSelectionChangeHandler = (selected: any) => {
		setSelectedEmployee(selected.value)
	}

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isPendingEmployeeDDL || isPendingSalaryProfilePermissionsData}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deletePaydayAdvanceField}
				cancelActionHandler={onCloseHandler}
				testId="paydayAdvance-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="PaydayAdvance"
										testId={`paydayAdvance`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<PaydayAdvanceInfoSetupFieldsPanel
											employeeDDL={employeeDDL && sortDDLByLabel(employeeDDL)}
											onEmployeeSelectionChangeHandler={onEmployeeSelectionChangeHandler}
										/>
									</StandardPanel>
								}
								toolbar={
									<PaydayAdvanceSetupToolbar
										onResetClicked={onResetClicked}
										// onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 2.5, lg: 2.5, xl: 2.5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									paydayAdvanceList && upadtedPaydayAdvanceListItems
										? upadtedPaydayAdvanceListItems
										: []
								}
								columns={[
									{ field: "label", headerName: "Type" },
									{ field: "dateNo", headerName: "Date" },
									{ field: "paidBy", headerName: "Paid By" },
									{ field: "status", headerName: "Status" },
									{ field: "amount", headerName: "Amount", align: "right" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onDeleteClicked}
								disabledDeleteIcon={salaryProfilePermissionsData && salaryProfilePermissionsData.lastPeriodProcessed !== null && salaryProfilePermissionsData.calculatePayroll ? false : true}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									paydayAdvanceList && paydayAdvanceList.total
										? paydayAdvanceList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				// showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archivePaydayAdvanceField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"paydayAdvance-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
